import type { TFunction } from 'i18next';

import type { ConversationStaffRef } from './types';

/**
 * The one staff-naming rule used everywhere a hub-resolved staff actor is shown — escalation
 * acknowledge/resolve, episode close, operator messages (design §13.2). A raw actor id is NEVER
 * rendered.
 */
export type StaffLabelKind = 'you' | 'named' | 'formerEmployee' | 'unknown' | 'none';

export interface StaffLabelResult {
  kind: StaffLabelKind;
  /** The rendered actor phrase — "you", the staff member's name, "a former employee", "an operator". */
  label: string;
  /** Set only for `kind === 'unknown'` when the directory itself could not be read (`UNAVAILABLE`). */
  tooltip?: string;
}

/**
 * Resolves how to name the staff actor behind a `ConversationStaffRef` (or, for an older BFF that has
 * not shipped the enrichment yet, a bare opaque `rawId` string), first match wins:
 *
 * 1. `ref.actorId === meId` (or `rawId === meId` with no ref at all) → `"you"` — decided by the id
 *    ALONE, before and regardless of the directory; a matching `name`/`email` no longer counts.
 * 2. `ref.resolution === 'MEMBER'` with a `displayName` → the name.
 * 3. `ref.resolution === 'NOT_A_MEMBER'` → "a former employee".
 * 4. `UNAVAILABLE`, or `MEMBER` with no name → "an operator", with a tooltip when the directory itself
 *    could not be read (`UNAVAILABLE`).
 * 5. No ref at all: an older BFF may still send only the bare id — "you" by an id match, else "an
 *    operator" (never a name, never "a former employee" — that needs the ref).
 * 6. Neither a ref nor a raw id (a system transition; nobody acted) → `null`, no actor phrase at all.
 */
export function staffLabel(
  ref: ConversationStaffRef | null | undefined,
  rawId: string | null | undefined,
  meId: string | null | undefined,
  t: TFunction,
): StaffLabelResult | null {
  if (!ref) {
    if (!rawId) {
      return null;
    }
    return meId && rawId === meId
      ? { kind: 'you', label: t('staff.you') }
      : { kind: 'unknown', label: t('staff.unknown') };
  }
  if (meId && ref.actorId === meId) {
    return { kind: 'you', label: t('staff.you') };
  }
  if (ref.resolution === 'MEMBER' && ref.displayName) {
    return { kind: 'named', label: ref.displayName };
  }
  if (ref.resolution === 'NOT_A_MEMBER') {
    return { kind: 'formerEmployee', label: t('staff.formerEmployee') };
  }
  return {
    kind: 'unknown',
    label: t('staff.unknown'),
    tooltip: ref.resolution === 'UNAVAILABLE' ? t('staff.unavailableTooltip') : undefined,
  };
}
